import { useReducer, useCallback, useEffect, useMemo, useRef } from 'react';

import type { SelectionMode } from '../report-requests.types';

export interface SelectionState {
  selections: Record<string, string[]>;
  selectAll: boolean;
}

type SelectionAction =
  | { type: 'TOGGLE_CONNECTION'; employerId: string; connectionId: string; mode: SelectionMode }
  | { type: 'TOGGLE_EMPLOYER'; employerId: string; connectionIds: string[] }
  | { type: 'TOGGLE_SELECT_ALL' }
  | { type: 'CLEAR' }
  | { type: 'SET_MODE' };

const initialState: SelectionState = {
  selections: {},
  selectAll: false,
};

function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'TOGGLE_CONNECTION': {
      if (action.mode === 'single') {
        const current = state.selections[action.employerId] || [];
        const isSelected = current.includes(action.connectionId);

        if (isSelected) {
          return { ...state, selections: {} };
        }

        return {
          ...state,
          selections: { [action.employerId]: [action.connectionId] },
        };
      }

      // Multi mode
      const current = state.selections[action.employerId] || [];
      const isSelected = current.includes(action.connectionId);
      const updated = isSelected ? current.filter(id => id !== action.connectionId) : [...current, action.connectionId];

      const newSelections = { ...state.selections };
      if (updated.length === 0) {
        delete newSelections[action.employerId];
      } else {
        newSelections[action.employerId] = updated;
      }

      return { ...state, selections: newSelections };
    }

    case 'TOGGLE_EMPLOYER': {
      const current = state.selections[action.employerId];
      const newSelections = { ...state.selections };

      // Employer-only mode (no connections)
      if (action.connectionIds.length === 0) {
        if (current !== undefined) {
          delete newSelections[action.employerId];
        } else {
          newSelections[action.employerId] = [];
        }
        return { ...state, selections: newSelections };
      }

      // With connections: toggle all on/off
      const allSelected =
        current !== undefined && current.length > 0 && action.connectionIds.every(id => current.includes(id));

      if (allSelected) {
        delete newSelections[action.employerId];
      } else {
        newSelections[action.employerId] = [...action.connectionIds];
      }

      return { ...state, selections: newSelections };
    }

    case 'TOGGLE_SELECT_ALL': {
      if (state.selectAll) {
        return { selections: {}, selectAll: false };
      }
      return { ...state, selectAll: true };
    }

    case 'CLEAR':
      return { selections: {}, selectAll: false };

    case 'SET_MODE':
      return { selections: {}, selectAll: false };

    default:
      return state;
  }
}

export function useSelection(mode: SelectionMode) {
  const [state, dispatch] = useReducer(selectionReducer, initialState);
  const modeRef = useRef(mode);

  useEffect(() => {
    if (modeRef.current !== mode) {
      modeRef.current = mode;
      dispatch({ type: 'SET_MODE' });
    }
  }, [mode]);

  const toggleConnection = useCallback(
    (employerId: string, connectionId: string) => {
      dispatch({ type: 'TOGGLE_CONNECTION', employerId, connectionId, mode });
    },
    [mode],
  );

  const toggleEmployer = useCallback(
    (employerId: string, connectionIds: string[]) => {
      if (mode === 'single') return;
      dispatch({ type: 'TOGGLE_EMPLOYER', employerId, connectionIds });
    },
    [mode],
  );

  const toggleSelectAll = useCallback(() => {
    if (mode === 'single') return;
    dispatch({ type: 'TOGGLE_SELECT_ALL' });
  }, [mode]);

  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
  }, []);

  const isConnectionSelected = useCallback(
    (employerId: string, connectionId: string): boolean => {
      if (state.selectAll) return true;
      const connections = state.selections[employerId];
      return connections ? connections.includes(connectionId) : false;
    },
    [state.selections, state.selectAll],
  );

  const isEmployerSelected = useCallback(
    (employerId: string): boolean => {
      if (state.selectAll) return true;
      return state.selections[employerId] !== undefined;
    },
    [state.selections, state.selectAll],
  );

  const selectedCount = useMemo(() => {
    const entries = Object.values(state.selections);
    // If any entry has connections, count connections; otherwise count employers
    const totalConnections = entries.reduce((sum, ids) => sum + ids.length, 0);
    return totalConnections > 0 ? totalConnections : entries.length;
  }, [state.selections]);

  return {
    state,
    toggleConnection,
    toggleEmployer,
    toggleSelectAll,
    clear,
    isConnectionSelected,
    isEmployerSelected,
    selectedCount,
  };
}
